import {DomainObject} from 'shared/store';
import { action, computed, observable, makeObservable } from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {t} from 'shared/core';
import TimeTrackingDimensionValue from './TimeTrackingDimensionValue';
import ClockEvent from './ClockEvent';

class TimesheetEntry extends DomainObject {
  uuid = uuidv4();
  date;
  showDate = false;
  startTime = null;
  endTime = null;
  startTimeString = '';
  endTimeString = '';
  note;
  approved = false;
  approvedStartTime = null;
  approvedEndTime = null;
  approvedManuallyEnteredHours = null;
  autosavedAt;
  errors = {};
  saveStatus;
  saveStatusTimeout;
  unpaidBreakMinutes = 0;
  manuallyEnteredHours;
  dimensionValues = [];
  clockInEvent;
  clockOutEvent;
  shiftsApplied = 0;

  _relations = {
    dimensionValues: [TimeTrackingDimensionValue],
    clockInEvent: ClockEvent,
    clockOutEvent: ClockEvent,
    _dates: ['date']
  };

  constructor(other) {
    super();

    makeObservable(this, {
      uuid: observable,
      date: observable,
      showDate: observable,
      startTime: observable,
      endTime: observable,
      startTimeString: observable,
      endTimeString: observable,
      note: observable,
      approved: observable,
      approvedStartTime: observable,
      approvedEndTime: observable,
      approvedManuallyEnteredHours: observable,
      autosavedAt: observable,
      errors: observable,
      saveStatus: observable,
      saveStatusTimeout: observable,
      unpaidBreakMinutes: observable,
      manuallyEnteredHours: observable,
      dimensionValues: observable,
      clockInEvent: observable,
      clockOutEvent: observable,
      shiftsApplied: observable,
      inputKey: computed,
      hours: computed,
      unpaidBreakHours: computed,
      hasNote: computed,
      hasBreak: computed,
      startTimeFromString: computed,
      endTimeFromString: computed,
      noValues: computed,
      setDimensionValue: action,
      setSubmitValues: action,
      markSaved: action
    });

    this.merge(other);

    // When retrieving the times we convert to string in UTC so that 9:00am UTC is 9:00a regardless of browser time zone
    // We work with it in the local time on the rest of the page, then just set it to be UTC before sending it back
    const timeFormat = t('components.dates.TIMESTAMP_FORMAT');
    if (this.startTime && !this.startTimeString) {
      this.startTimeString = moment(this.startTime).utc().format(timeFormat);
    }
    if (this.endTime && !this.endTimeString) {
      this.endTimeString = moment(this.endTime).utc().format(timeFormat);
    }
  }

  dimensionValueFor(dimensionId) {
    return _.find(this.dimensionValues, {dimensionId});
  }

  setDimensionValue(dimension, value) {
    const others = this.dimensionValues.filter(dimensionValue => dimensionValue.dimensionId !== dimension.id);
    this.dimensionValues = value ? [...others, value] : others;
  }

  setSubmitValues(payPeriod) {
    this.startTime = this.startTimeFromString;
    this.endTime = this.endTimeFromString;
    this.payPeriod = payPeriod;
  }

  markSaved() {
    this.saveStatus = 'saved';
    clearTimeout(this.saveStatusTimeout);
    this.saveStatusTimeout = setTimeout(() => {
      this.saveStatus = 'fadingAway';
      this.saveStatusTimeout = setTimeout(() => {
        this.saveStatus = null;
      }, 500);
    }, 1000);
  }

  get inputKey() {
    return `${this.uuid}-${this.shiftsApplied}`;
  }

  get hours() {
    if (this.manuallyEnteredHours !== undefined) {
      return this.manuallyEnteredHours;
    }

    if (!this.startTimeString || !this.endTimeString) {
      return null;
    }

    return Math.max(Math.round((((this.endTimeFromString - this.startTimeFromString) / 3600000) - (this.unpaidBreakMinutes / 60)) * 100) / 100, 0);
  }

  get unpaidBreakHours() {
    return Math.round((this.unpaidBreakMinutes / 60) * 100) / 100;
  }

  get hasNote() {
    return !_.isEmpty(this.note);
  }

  get hasBreak() {
    return !!this.unpaidBreakMinutes;
  }

  get startTimeFromString() {
    if (!this.startTimeString) {
      return null;
    }

    // The frontend works in browser local time for everything, but we want the backend to store the time in UTC
    // .utc(true) sets the timezone to UTC without changing the time, so the 9:00a that the user enters
    // gets saved as 9:00am UTC regardless of the browser's timezone
    return moment(`${moment(this.date).format('YYYY-MM-DD')} ${this.startTimeString}`, ['YYYY-MM-DD h:m a']).utc(true);
  }

  get endTimeFromString() {
    if (!this.endTimeString) {
      return null;
    }

    const date = moment(this.date);
    if (moment(this.endTimeString, ['h:m a', 'H:m']) < moment(this.startTimeString, ['h:m a', 'H:m'])) {
      date.add(1, 'd');
    }
    return moment(`${date.format('YYYY-MM-DD')} ${this.endTimeString}`, ['YYYY-MM-DD h:m a']).utc(true);
  }

  get noValues() {
    return !this.startTime && !this.manuallyEnteredHours;
  }
}

export default TimesheetEntry;
